import React, { useState } from "react";
import apiClient from "../api/apiClient";
import DataTable from "react-data-table-component";

const CierreDocumentos = () => {
  const [docType, setDocType] = useState("Factura");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [documents, setDocuments] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [filterText, setFilterText] = useState("");

  // Filtrado dinámico sobre todos los campos visibles
  const filteredDocuments = documents.filter((row) => {
    if (!filterText) return true;
    const search = filterText.toLowerCase();
    return (
      String(row.DocEntry ?? "").toLowerCase().includes(search) ||
      String(row.DocNum ?? "").toLowerCase().includes(search) ||
      String(row.FolioNumber ?? "").toLowerCase().includes(search) ||
      String(row.CardCode ?? "").toLowerCase().includes(search) ||
      String(row.CardName ?? "").toLowerCase().includes(search) ||
      String(row.DocTotal ?? "").toLowerCase().includes(search)
    );
  });

  const fetchOpenDocuments = async () => {
    setLoading(true);
    setMessage("");
    setError("");
    try {
      const sapUser = JSON.parse(localStorage.getItem("sapUser") || "null");
      const companyDB = sapUser?.CompanyDB;

      const response = await apiClient.get("/documents/open", {
        params: { DocType: docType, StartDate: startDate, EndDate: endDate, CompanyDB: companyDB },
      });
      const data = Array.isArray(response.data) ? response.data : (response.data?.documents || []);
      setDocuments(data);
      setSelectedRows([]);
    } catch (err) {
      setError(err.response?.data?.error || "Error al listar documentos abiertos por rango de fecha.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkClose = async () => {
    if (selectedRows.length === 0) {
      setError("Selecciona al menos un documento para cerrar.");
      return;
    }
    setClosing(true);
    setMessage("");
    setError("");
    try {
      const payload = selectedRows.map((row) => ({ DocEntry: row.DocEntry, DocType: docType }));
      const response = await apiClient.post("/documents/close/bulk", payload, { timeout: 600000 });
      const summary = response.data?.summary;
      const successCount = summary?.successCount ?? 0;
      const failedCount = summary?.failedCount ?? 0;
      setMessage(`Cierre masivo completado. Exitosos: ${successCount}, Fallidos: ${failedCount}.`);

      // Opcional: remover de la grilla los documentos exitosamente cerrados
      const successfulDocEntries = new Set(
        (response.data?.results || [])
          .filter((r) => r.success)
          .map((r) => r.DocEntry)
      );
      if (successfulDocEntries.size > 0) {
        setDocuments((prev) => prev.filter((d) => !successfulDocEntries.has(d.DocEntry)));
      }
      setSelectedRows([]);
    } catch (err) {
      setMessage("");
      setError(err.response?.data?.error || "Error inesperado al cerrar documentos.");
    } finally {
      setClosing(false);
    }
  };

  const handleBulkCancel = async () => {
    setShowCancelConfirm(false);
    setCancelling(true);
    setMessage("");
    setError("");
    try {
      const payload = selectedRows.map((row) => ({ DocEntry: row.DocEntry, DocType: docType }));
      const response = await apiClient.post("/documents/cancel/bulk", payload, { timeout: 600000 });
      const summary = response.data?.summary;
      const successCount = summary?.successCount ?? 0;
      const failedCount = summary?.failedCount ?? 0;
      setMessage(`Cancelación masiva completada. Exitosos: ${successCount}, Fallidos: ${failedCount}.`);

      const successfulDocEntries = new Set(
        (response.data?.results || [])
          .filter((r) => r.success)
          .map((r) => r.DocEntry)
      );
      if (successfulDocEntries.size > 0) {
        setDocuments((prev) => prev.filter((d) => !successfulDocEntries.has(d.DocEntry)));
      }
      setSelectedRows([]);
    } catch (err) {
      setMessage("");
      setError(err.response?.data?.error || "Error inesperado al cancelar documentos.");
    } finally {
      setCancelling(false);
    }
  };

  const columns = [
    {
      name: "ID Interno",
      selector: (row) => row.DocEntry,
      sortable: true,
      width: "115px",
    },
    {
      name: "Nro. Documento",
      selector: (row) => row.DocNum,
      sortable: true,
      width: "140px",
    },
    {
      name: "Nro. Folio",
      selector: (row) => row.FolioNumber,
      sortable: true,
      width: "115px",
    },
    {
      name: "Cod. Cliente",
      selector: (row) => row.CardCode,
      width: "110px",
    },
    {
      name: "Cliente",
      selector: (row) => row.CardName,
      width: "300px",
    },
    {
      name: "Estado",
      selector: (row) => {
        if (row.DocumentStatus === "bost_Open") {
          return row.PaidToDate > 0 ? "Importe Aplicado" : "Abierto";
        } else if (row.DocumentStatus === "bost_Close") {
          return row.Cancelled === "tYES" ? "Cancelado" : "Cerrado";
        }
        return "Desconocido";
      },
      width: "150px",
    },
    {
      name: "Monto",
      selector: (row) => {
        try {
          return Number(row.DocTotal).toLocaleString("de-DE");
        } catch {
          return row.DocTotal;
        }
      },
      sortable: true,
      width: "140px",
    },
  ];

  return (
    <div className="container mt-5">
      <h2>Cierre de Documentos</h2>
      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={(e) => { e.preventDefault(); fetchOpenDocuments(); }} className="mb-4">
        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <label htmlFor="docType" className="form-label">Tipo de Documento</label>
            <select
              className="form-control"
              id="docType"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
            >
              <option value="Factura">Factura</option>
              <option value="Nota de crédito">Nota de Crédito</option>
              <option value="Entrega">Entrega</option>
              <option value="Pedido">Orden de Ventas</option>
              <option value="Devolucion">Devolucion de Mercaderías</option>
              <option value="Traslados">Traslados</option>
            </select>
          </div>
          <div className="col-md-3">
            <label htmlFor="startDate" className="form-label">Fecha inicio</label>
            <input
              type="date"
              className="form-control"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label htmlFor="endDate" className="form-label">Fecha fin</label>
            <input
              type="date"
              className="form-control"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Buscando..." : "Buscar"}
            </button>
          </div>
        </div>
      </form>

      <div className="mb-2 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Buscar en resultados..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ width: "250px" }}
          />
          {filterText && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setFilterText("")}
              title="Limpiar filtro"
            >
              ✕
            </button>
          )}
          <span className="small text-muted">
            {filterText
              ? `${filteredDocuments.length} de ${documents.length} documentos`
              : `${documents.length} documentos`}
          </span>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={() => setShowCancelConfirm(true)}
            disabled={cancelling || closing || selectedRows.length === 0}
            title={selectedRows.length === 0 ? "Selecciona documentos" : "Cancelar documentos seleccionados"}
          >
            {cancelling ? "Cancelando..." : `Cancelar seleccionados (${selectedRows.length})`}
          </button>
          <button
            type="button"
            className="btn btn-outline-success btn-sm"
            onClick={handleBulkClose}
            disabled={closing || cancelling || selectedRows.length === 0}
            title={selectedRows.length === 0 ? "Selecciona documentos" : "Cerrar documentos seleccionados"}
          >
            {closing ? "Cerrando..." : `Cerrar seleccionados (${selectedRows.length})`}
          </button>
        </div>
      </div>

      {/* Modal de confirmación para cancelar */}
      {showCancelConfirm && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">Confirmar Cancelación</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowCancelConfirm(false)} />
              </div>
              <div className="modal-body">
                <p>¿Estás seguro de que deseas <strong>cancelar</strong> los <strong>{selectedRows.length}</strong> documento(s) seleccionado(s)?</p>
                <p className="text-danger mb-0"><small>Esta acción eliminará el folio y cancelará los documentos en SAP. No se puede deshacer.</small></p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCancelConfirm(false)}>No, volver</button>
                <button type="button" className="btn btn-danger" onClick={handleBulkCancel}>Sí, cancelar documentos</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={filteredDocuments}
        pagination
        highlightOnHover
        responsive
        striped
        selectableRows
        onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
        selectableRowsHighlight
      />
    </div>
  );
};

export default CierreDocumentos;
